import type { MetricLegajo } from '../../services/projectAutofillDraftService.ts'

export type MetricLegajoDisplayKind = 'spreadsheet' | 'image' | 'pdf' | 'other'

export function classifyMetricLegajoDisplay(legajo: MetricLegajo): MetricLegajoDisplayKind {
  if (legajo.isSpreadsheet) return 'spreadsheet'
  const mime = (legajo.mimeType || '').trim().toLowerCase()
  const name = (legajo.originalName || '').trim().toLowerCase()
  if (mime.startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(name)) return 'image'
  if (
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    mime === 'application/vnd.ms-excel' ||
    /\.xlsx?$/i.test(name)
  ) {
    return 'spreadsheet'
  }
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'pdf'
  if (legajo.pages.length > 0) return 'pdf'
  return 'other'
}

export function metricLegajoPublishHint(
  kind: MetricLegajoDisplayKind,
  legajo: MetricLegajo,
  summarizePages: (pages: number[]) => string
): string {
  const label = legajo.originalName?.trim()
  const namePart = label ? ` (${label})` : ''
  switch (kind) {
    case 'spreadsheet':
      return `Detectamos un cómputo en planilla${namePart}. Al publicar se envía ese archivo.`
    case 'image':
      return `Detectamos un cómputo en imagen${namePart}. Al publicar se envía esa imagen.`
    case 'pdf': {
      const pages = legajo.pages.length > 0 ? summarizePages(legajo.pages) : ''
      return pages
        ? `Detectamos cómputo en el archivo${namePart} (páginas ${pages}). Al publicar se envían esas páginas.`
        : `Detectamos cómputo en el archivo${namePart}. Al publicar se envía ese PDF.`
    }
    default:
      return `Detectamos cómputo en la documentación${namePart}. Al publicar se envía el archivo asignado.`
  }
}

export function metricLegajoSourceSectionTitle(kind: MetricLegajoDisplayKind | null | undefined): string {
  if (kind === 'spreadsheet') return 'Planilla original'
  if (kind === 'image') return 'Imagen original'
  if (kind === 'pdf') return 'Documento original'
  return 'Archivo original'
}

export const METRIC_IMPORT_GRID_BUTTON_LABEL = 'Armar grilla desde el archivo'

export function metricLegajoReadonlyNotice(kind: MetricLegajoDisplayKind | null | undefined): string {
  const intro =
    kind === 'spreadsheet'
      ? 'Vista de solo lectura: esta planilla es la que enviaremos con el rubro. Podés descargarla para revisarla.'
      : kind === 'image'
        ? 'Vista de solo lectura: esta imagen es la que enviaremos con el rubro.'
        : 'Vista de solo lectura: este archivo es el que enviaremos con el rubro.'
  return `${intro} Para editar ítems y cantidades en Edify, usá "${METRIC_IMPORT_GRID_BUTTON_LABEL}" o "Cargar grilla manualmente" más abajo.`
}

export function metricImportGridTagline(): string {
  return 'Si querés editar ítems y cantidades acá, podemos armar la grilla a partir del archivo detectado.'
}

export function metricManualGridTagline(): string {
  return 'Si preferís no usar el archivo detectado, podés cargar una grilla vacía y completarla a mano.'
}

export function metricLegajoReadonlyModalBanner(): string {
  return `Este cómputo está en tu documentación. Acá solo podés consultarlo; al publicar se envía ese archivo. Para cambiar ítems y cantidades en Edify, volvé al rubro y elegí "${METRIC_IMPORT_GRID_BUTTON_LABEL}" o "Cargar grilla manualmente".`
}
